import React, { useState } from 'react';
import { useAuth } from '../../services/AuthContext';
import { 
  AppBar, 
  Box, 
  Toolbar, 
  Typography, 
  IconButton, 
  Tooltip,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
  Avatar
} from '@mui/material';
import {
  Menu as MenuIcon,
  People as PeopleIcon,
  LocationOn as LocationIcon,
  CheckCircle as CheckIcon,
  LocalGasStation as FuelIcon,
  Upload as UploadIcon,
  Analytics as AnalyticsIcon,
  TrendingUp as MetricsIcon,
  Description as DocumentIcon,
  ChevronRight as ArrowIcon,
  LocalShipping as EquipmentIcon,
  Assessment as ReportsIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Dataset,
  LocalShipping,
  Schedule,
  Groups,
  LocationOn,
  PlaceOutlined,
  Category,
  Warning,
  FactCheck,
  Build,
  EventBusy,
  LocalGasStation,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useThemeMode } from '../../contexts/ThemeContext';

const drawerWidth = 280;


const navItems = [
  { label: 'Dashboard', icon: <AnalyticsIcon />, path: '/' },

  {
    label: 'Common Data',
    icon: <Dataset />,
    path: '/commondata',
    children: [
      { label: 'Operators', icon: <PeopleIcon />, path: '/users' },
      { label: 'Equipments', icon: <LocalShipping />, path: '/commondata/equipments' },
      { label: 'Shifts', icon: <Schedule />, path: '/commondata/shifts' },
      { label: 'Crew', icon: <Groups />, path: '/commondata/crew' },
    ]
  },
  { 
    label: 'Haul Master Live', 
    icon: <MetricsIcon />, 
    path: '/haulmasterlive',
    children: [
      { label: 'Load Locations', icon: <LocationOn />, path: '/loadlocations' },
      { label: 'Dump Locations', icon: <PlaceOutlined />, path: '/dumplocations' },
      { label: 'Materials', icon: <Category />, path: '/materials' },
    ]
  },
  { 
    label: 'Pre-Checks & Forms', 
    icon: <DocumentIcon />, 
    path: '/prechecksandforms',
    children: [
      { label: 'SMART FLHA', icon: <Warning />, path: '/prechecksandforms/flha' },
      { label: 'Equipment Pre Check', icon: <FactCheck />, path: '/prechecksandforms/equipmentprecheck' },
      { label: 'Equipment Breakdown', icon: <Build />, path: '/prechecksandforms/equipmentbreakdown' },
      { label: 'Non-Productive Time', icon: <EventBusy />, path: '/prechecksandforms/nptreasons' },
    ]
  },

  { label: 'Fuel Types and Sources', icon: <LocalGasStation />, path: '/fueltypesandsources' },
  { label: 'Reports', icon: <ReportsIcon />, path: '/reports' },
];

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { mode, toggleTheme } = useThemeMode();
  const { user, logout } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openMenus, setOpenMenus] = useState<{ [key: string]: boolean }>({});
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const toggleMenu = (label: string) => {
    setOpenMenus(prev => ({ ...prev, [label]: !prev[label] }));
  };

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setDrawerOpen(false);
  };

  const drawer = (
    <Box 
      sx={{ 
        width: drawerWidth, 
        height: '100%',
        backgroundColor: mode === 'light' ? '#ffffff' : '#1e293b',
        color: mode === 'light' ? '#0f172a' : '#f1f5f9',
      }}
    >
      {/* Drawer Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        p: 2.5,
        borderBottom: mode === 'light' ? '1px solid #e2e8f0' : '1px solid #334155',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ 
            width: 40, 
            height: 40, 
            backgroundColor: '#667eea', 
            borderRadius: 1.5, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            mr: 1.5
          }}>
            <EquipmentIcon sx={{ fontSize: 24, color: theme.palette.text.primary }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              MineProd
            </Typography>
            <Typography variant="caption" sx={{ color: mode === 'light' ? '#64748b' : '#94a3b8', fontSize: '0.7rem' }}>
              Engineering Portal
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={toggleDrawer} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Navigation Items */}
      <List sx={{ px: 2, py: 2 }}>
  {navItems.map((item, index) => {
    const hasChildren = Array.isArray(item.children);
    const isOpen = openMenus[item.label];
    const isActive = location.pathname === item.path;

    return (
      <React.Fragment key={index}>
        <ListItem disablePadding sx={{ mb: 0.5 }}>
          <ListItemButton
            onClick={() => handleNavigation(item.path)}
            sx={{
              borderRadius: 1.5,
              py: 1.5,
              px: 2,
              backgroundColor: isActive ? '#667eea' : 'transparent',
              color: isActive ? 'white' : mode === 'light' ? '#64748b' : '#94a3b8',
              '&:hover': {
                backgroundColor: isActive
                  ? '#4c5fd7'
                  : mode === 'light'
                  ? 'rgba(249, 115, 22, 0.08)'
                  : 'rgba(249, 115, 22, 0.12)',
                color: isActive ? 'white' : '#667eea',
              },
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            {/* Left side: icon + label */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: isActive ? 600 : 500,
                }}
              />
            </Box>

            {/* Right side: plus icon */}
            {hasChildren && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation(); // prevent navigation
                  toggleMenu(item.label);
                }}
                sx={{ color: 'inherit' }}
              >
                {isOpen ? <CloseIcon /> : <AddIcon />}
              </IconButton>
            )}
          </ListItemButton>
        </ListItem>

        {/* Child items */}
        {hasChildren && isOpen && (
          <List sx={{ pl: 6, py: 0 }}>
            {item.children!.map((child, childIndex) => {
              const isChildActive = location.pathname === child.path;

              return (
                <ListItem key={childIndex} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => handleNavigation(child.path)}
                    sx={{
                      borderRadius: 1.5,
                      py: 1,
                      px: 2,
                      backgroundColor: isChildActive ? '#667eea' : 'transparent',
                      color: isChildActive ? 'white' : mode === 'light' ? '#64748b' : '#94a3b8',
                      '&:hover': {
                        backgroundColor: isChildActive
                          ? '#4c5fd7'
                          : mode === 'light'
                          ? 'rgba(249, 115, 22, 0.08)'
                          : 'rgba(249, 115, 22, 0.12)',
                        color: isChildActive ? 'white' : '#667eea',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                      {child.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={child.label}
                      primaryTypographyProps={{
                        fontSize: '0.85rem',
                        fontWeight: isChildActive ? 600 : 500,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}
      </React.Fragment>
    );
  })}
</List>
    </Box>
  );

  return (
    <>
      <AppBar 
        position="static" 
        elevation={0} 
        sx={{ 
          backgroundColor: mode === 'light' ? '#ffffff' : '#1e293b', 
          borderBottom: mode === 'light' ? '1px solid #e2e8f0' : '1px solid #334155',
          color: mode === 'light' ? '#0f172a' : '#f1f5f9',
        }}
      >
        <Toolbar sx={{ minHeight: 64, px: 2 }}>
          {/* Hamburger Menu Button */}
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={toggleDrawer}
            sx={{ 
              mr: 2,
              '&:hover': {
                backgroundColor: mode === 'light' ? 'rgba(249, 115, 22, 0.08)' : 'rgba(249, 115, 22, 0.12)',
                color: '#667eea',
              },
            }}
          >
            <MenuIcon />
          </IconButton>

          {/* Logo and Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Box sx={{ 
              width: 40, 
              height: 40, 
              backgroundColor: '#667eea', 
              borderRadius: 1.5, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              mr: 1.5
            }}>
              <EquipmentIcon sx={{ fontSize: 24, color: theme.palette.text.primary }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: '1.1rem' }}>
                MineProd
              </Typography>
              <Typography variant="caption" sx={{ color: mode === 'light' ? '#64748b' : '#94a3b8', fontSize: '0.7rem' }}>
                Engineering Portal
              </Typography>
            </Box>
          </Box>

          {/* Theme Toggle */}
          <Tooltip title={mode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}>
            <IconButton
              onClick={toggleTheme}
              sx={{
                color: mode === 'light' ? '#64748b' : '#94a3b8',
                '&:hover': {
                  backgroundColor: mode === 'light' ? 'rgba(249, 115, 22, 0.08)' : 'rgba(249, 115, 22, 0.12)',
                  color: '#667eea',
                },
              }}
            >
              {mode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
            </IconButton>
          </Tooltip>
          {/* User Section */}
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
            {user ? (
              <>
                <Tooltip title="Account">
                  <Box
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      '&:hover': {
                        backgroundColor: mode === 'light' ? '#f1f5f9' : '#334155',
                      },
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: '#667eea',
                        fontSize: '0.9rem',
                        mr: 1,
                      }}
                    >
                      {user.firstName?.[0]}
                    </Avatar>

                    <Typography
                      variant="body2"
                      sx={{
                        color: mode === 'light' ? '#475569' : '#cbd5e1',
                        fontWeight: 500,
                        fontSize: '0.85rem',
                      }}
                    >
                      {user.firstName}
                    </Typography>
                  </Box>
                </Tooltip>

                {/* Dropdown Menu */}
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={() => setAnchorEl(null)}
                  PaperProps={{
                    elevation: 3,
                    sx: {
                      mt: 1,
                      minWidth: 180,
                      borderRadius: 2,
                      backgroundColor: mode === 'light' ? '#ffffff' : '#1e293b',
                      color: mode === 'light' ? '#0f172a' : '#f1f5f9',
                    },
                  }}
                >
                  <MenuItem disabled>
                    <Typography sx={{ fontSize: '0.85rem', opacity: 0.8 }}>
                      {user.firstName} {user.lastName} ({user.roleName})
                    </Typography>
                  </MenuItem>

                  <Divider />

                  <MenuItem onClick={logout}>
                    <Typography sx={{ fontSize: '0.9rem' }}>Logout</Typography>
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <Typography
                onClick={() => handleNavigation('/login')}
                sx={{
                  cursor: 'pointer',
                  color: mode === 'light' ? '#64748b' : '#94a3b8',
                  '&:hover': { color: '#667eea' },
                  fontWeight: 500,
                }}
              >
                Login
              </Typography>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Sidebar Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer}
        sx={{
          '& .MuiDrawer-paper': {
            backgroundColor: mode === 'light' ? '#ffffff' : '#1e293b',
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Header;